import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { AuditLog, AuditLogService } from "../../services/audit-log/audit-log.service";

@Component({
    selector: "audit-logging-table",
    templateUrl: "./audit-logging-table.component.html",
    styleUrls: ["./audit-logging-table.component.scss"],
    standalone: false
})
export class AuditLoggingTable implements OnInit, AfterViewInit, OnChanges {
    @Input() data: AuditLog[] = [];
    @Input() totalItems = 0;
    @Input() pageSize = 5;
    @Input() pageIndex = 0;
    @Input() isGrouped = false;
    @Input() search = "";
    @Output() pageChange = new EventEmitter<number>();
    @Output() pageSizeChange = new EventEmitter<number>();
    @Output() sortChanged = new EventEmitter<Sort>();
    @Output() openUrlEvent = new EventEmitter<AuditLog>();

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    displayedColumns: string[] = ["date", "user", "activity"];
    dataSource = new MatTableDataSource<AuditLog>();

    groupedTableDataSource = new MatTableDataSource<GroupedRow>();
    originalGroupedData: GroupedData[] = [];
    groupedData: GroupedData[] = [];

    totalPaginatedItems = 0;

    get cloudProcessEnabled(): boolean {
        return this.auditLogService.cloudProcessEnabled;
    }
    set cloudProcessEnabled(value: boolean) {
        this.auditLogService.cloudProcessEnabled = value;
    }

    constructor(private cdr: ChangeDetectorRef, private auditLogService: AuditLogService) {}

    ngOnInit(): void {}

    ngAfterViewInit(): void {
        this.paginator.page.subscribe(({ pageIndex, pageSize }: PageEvent) => {
            this.pageIndex = pageIndex;
            this.pageSize = pageSize;
            this.pageChange.emit(pageIndex);
            this.pageSizeChange.emit(pageSize);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["data"]?.currentValue) {
            this.handleDataChange();
        }

        if (changes["totalItems"]?.currentValue) {
            this.totalItems = changes["totalItems"].currentValue;
        }

        if (changes["isGrouped"]) {
            this.handleGroupingToggle();
        }

        if (changes["search"]) {
            this.dataSource.filter = changes["search"].currentValue || "";
            this.groupedTableDataSource.filter =
                changes["search"].currentValue || "";
        }
    }

    private handleDataChange(): void {
        const processedData = this.cloudProcessEnabled
            ? [...this.data]
            : this.data.filter((log) => log.user !== "Unknown");

        if (this.isGrouped) {
            this.groupedData = this.getGroupedData(processedData);
            this.totalPaginatedItems = this.originalGroupedData.length;
        } else {
            this.dataSource = new MatTableDataSource(processedData);

            this.dataSource.sort = this.sort;
            this.dataSource.sortingDataAccessor = (item, property) =>
                property === "activity"
                    ? item.activity.activity
                    : item[property];

            this.dataSource.filterPredicate = (
                data: AuditLog,
                filter: string
            ): boolean =>
                this.flattenObject(data).includes(filter.trim().toLowerCase());

            if (this.search) {
                this.dataSource.filter = this.search;
            }

            this.totalPaginatedItems = processedData.length;
            this.dataSource.paginator = this.paginator;
        }

        if (this.isGrouped && this.search) {
            this.groupedTableDataSource.filter = this.search;
        }
    }

    private flattenObject(obj: any): string {
        if (obj == null) return "";
        if (typeof obj === "object") {
            return Object.values(obj)
                .map((val) => this.flattenObject(val))
                .join(" ")
                .toLowerCase();
        }
        return String(obj).toLowerCase();
    }

    private handleGroupingToggle(): void {
        this.pageIndex = 0;

        if (!this.isGrouped) {
            this.dataSource.paginator = this.paginator;
            this.dataSource.sort = this.sort;
            this.totalPaginatedItems = this.dataSource.data.length;
        } else {
            this.groupedData = this.getGroupedData(this.dataSource.data);
            this.totalPaginatedItems = this.groupedData.length;
        }
    }

    getGroupedData(logs: AuditLog[]): GroupedData[] {
        const groupedData: GroupedData[] = [];
        const userLogsMap = new Map<string, AuditLog[]>();

        logs.forEach((log) => {
            const userLogs = userLogsMap.get(log.userId) || [];
            userLogs.push(log);
            userLogsMap.set(log.userId, userLogs);
        });

        userLogsMap.forEach((userLogs, userId) => {
            let sessionIndex = 0;
            let currentLogs: AuditLog[] = [];

            userLogs.forEach((log, i) => {
                const nextLog = userLogs[i + 1];
                currentLogs.push(log);

                if (nextLog?.activity.caption === "Logged Out") {
                    groupedData.push(
                        this.createGroup(
                            userId,
                            log.user,
                            currentLogs,
                            sessionIndex++
                        )
                    );
                    currentLogs = [];
                }
            });

            if (currentLogs.length > 0) {
                groupedData.push(
                    this.createGroup(
                        userId,
                        currentLogs[0].user,
                        currentLogs,
                        sessionIndex
                    )
                );
            }
        });

        this.originalGroupedData = groupedData;
        this.applyPagination();

        return groupedData;
    }

    private createGroup(
        userId: string,
        user: string,
        logs: AuditLog[],
        sessionIndex: number
    ): GroupedData {
        const startDate = logs[0]?.date?.toUTCString() ?? "-";
        const endDate = logs[logs.length - 1]?.date?.toUTCString() ?? "-";

        return {
            groupId: `${userId}-${sessionIndex}`,
            user,
            userId,
            logs,
            startDate,
            endDate,
            expanded: false,
        };
    }

    toggleGroup(groupId: string): void {
        const group = this.groupedData.find((g) => g.groupId === groupId);
        if (group) {
            group.expanded = !group.expanded;
            this.applyPagination();
        }
    }

    applyPagination(): void {
        const start = this.pageIndex * this.pageSize;
        const end = start + this.pageSize;

        this.groupedData = [...this.originalGroupedData];
        this.totalPaginatedItems = this.originalGroupedData.length;

        this.generateGroupedTableData(this.groupedData);
        this.cdr.detectChanges();
    }

    readableNavigationFormat(log: AuditLog): string {
        let returnText = "";
        if(log.activity && log.activity.redirection && log.activity.redirection.text) {
            returnText = this.auditLogService.matchUrlWithDetailedActivity(log.activity.redirection.text);
        }
        return returnText;
    }

    readableLink(link: string): string {
        let returnText = link;
        if(link && link.indexOf("unit?")>0) {
            returnText = link.substring(0, link.indexOf("?"));
        }
        return returnText;
    }

    generateGroupedTableData(groupedData: GroupedData[]) {
        this.groupedTableDataSource.data = groupedData.flatMap((group) => {
            const groupId = group.groupId;
            const groupRow: GroupedRow = {
                isGroup: true,
                user: {
                    fullname: group.user,
                },
                expanded: !!group.expanded,
                groupId,
                startDate: group.startDate,
                endDate: group.endDate,
            };

            const logRows: GroupedRow[] = group.expanded
                ? group.logs.map((log) => ({
                      isGroup: false as const,
                      log,
                      groupId,
                      startDate: group.startDate,
                      endDate: group.endDate,
                  }))
                : [];

            return [groupRow, ...logRows];
        });
        this.groupedTableDataSource.paginator = this.paginator;
    }

    onSortChange(sort: Sort): void {
        this.sortChanged.emit(sort);
    }

    openUrl(log: AuditLog): void {
        if (log.activity.redirection.link) {
            this.openUrlEvent.emit(log);
        }
    }

    isOverflowed(el: HTMLElement): boolean {
        return (
            el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight
        );
    }

    toggleCloudProcess(): void {
        this.cloudProcessEnabled = !this.cloudProcessEnabled;
        const logs = this.cloudProcessEnabled
            ? this.data
            : this.data.filter((log) => log.user !== "Unknown");
        if (this.isGrouped) {
            this.groupedData = this.getGroupedData(logs);
        } else {
            this.dataSource.data = logs;
            this.totalPaginatedItems = logs.length;
        }
    }

    isGroupHeader = (_: number, row: GroupedRow): boolean => row.isGroup;
    isDataRow = (_: number, row: GroupedRow): boolean => !row.isGroup;
}

type GroupedData = {
    groupId: string;
    user: string;
    userId: string;
    logs: AuditLog[];
    startDate: string;
    endDate: string;
    expanded?: boolean;
};

type GroupedRow =
    | {
          isGroup: true;
          user: { fullname: string };
          expanded: boolean;
          groupId: string;
          startDate: string;
          endDate: string;
      }
    | {
          isGroup: false;
          log: AuditLog;
          groupId: string;
          startDate: string;
          endDate: string;
      };
